export type ProviderAssignment = {
  experiment_id: string;
  experiment_version: string;
  assignment_id: string;
  scope: "customer" | "launch" | "internal" | "excluded";
  variant: "h3_local" | "veo" | "excluded";
  basis_points: number;
  bucket: number | null;
  eligibility_reason: string;
  config_release_id: string;
  config_release_digest: string;
  route_contract_digest: string;
};

export type I2VProviderSummary = {
  schema_version: "i2v-provider-summary.v1";
  assignment?: ProviderAssignment | null;
  evidence: "selected_manifest" | "assignment_only";
  has_h3: boolean;
  provider_counts?: Record<string, number>;
  h3_scene_count?: number;
  generated_scene_count?: number;
  fallback_scene_count?: number;
  static_scene_count?: number;
  unknown_scene_count?: number;
  compute_plane: "fal" | "runpod" | "local_gpu" | "mixed" | "unknown";
};

export function parseProviderSummary(
  value: unknown,
): I2VProviderSummary | null {
  if (!value || typeof value !== "object") return null;
  const row = value as Record<string, unknown>;
  if (
    row.schema_version !== "i2v-provider-summary.v1" ||
    typeof row.has_h3 !== "boolean"
  )
    return null;
  return row as I2VProviderSummary;
}

/** Provider id of self-hosted LTX-2.5, the real-estate i2v lane from 2026-10-04. */
export const LTX25_PROVIDER_ID = "ltx25_comfy";

/**
 * Selected clips generated by self-hosted LTX-2.5. The summary has no
 * `has_ltx` flag (the orchestrator's `has_h3` counts H3 only), so this reads
 * `provider_counts.ltx25_comfy`; 0 when absent or malformed.
 */
export function ltx25SceneCount(summary?: I2VProviderSummary | null): number {
  const count = Number(summary?.provider_counts?.[LTX25_PROVIDER_ID] ?? 0);
  return Number.isFinite(count) && count > 0 ? count : 0;
}

export const computePlaneLabel = (plane?: string) =>
  ({
    fal: "fal AI",
    runpod: "RunPod",
    local_gpu: "Local GPU",
    mixed: "Mixed",
    unknown: "Unknown",
  })[plane ?? "unknown"] ?? "Unknown";

export type ProviderEcho = {
  workload_id: string;
  title?: string;
  scope: string;
  variant: string;
  status: string;
  created_at: string;
  rating?: number | null;
  rating_comment?: string;
  final_video_s3_key?: string;
  final_video_s3_bucket?: string;
  i2v_provider_summary: I2VProviderSummary;
};

export type ProviderMetrics = {
  variant: string;
  assigned_users: number;
  echoes: number;
  exposed_echoes: number;
  generated_echoes: number;
  delivered_echoes: number;
  failed_echoes: number;
  rated_echoes: number;
  rated_users: number;
  rating_response_rate: number | null;
  mean_rating: number | null;
  mean_user_rating: number | null;
  rating_distribution: Record<string, number>;
  h3_scenes: number;
  generated_scenes: number;
  fallback_scenes: number;
  static_scenes: number;
  missing_manifest_echoes: number;
};
